import React from 'react';

import {
  TEMPLATE_ROW_COPY,
  fieldTemplateRowText,
} from '../../utils/rxTemplates';
import { ListGlyph, Chevron, OPENER_TEAL } from './TemplateChipRow';

/**
 * ONE template row for a field's dropdown (D25 / variant F, F2).
 *
 * The card is the one D10 already shipped inside the medicine box — a filled
 * teal card with its own icon tile, the template's NAME where a drug name would
 * be and a solid count pill — extracted so Symptoms, Diagnosis, Medicines and
 * Tests all render the SAME row instead of four look-alikes. A doctor must
 * never add 3 medicines thinking he added 1, in any of the four fields.
 *
 * Deliberate deviations from the panels around it, both argued on the
 * designer's page 10:
 *  - the group heading is TEAL ink (every other heading is muted grey): it is
 *    the only heading naming a *saved* thing rather than a data source. Ink
 *    only — no tinted band, no second surface.
 *  - `See all n` is a real button with a solid day AND night background, so it
 *    is visible without hover (button-visibility rule).
 *
 * Narrow host (the accordion, mobile, the two-column pad's left column): the
 * sub-line and the `Saved` badge are dropped — the teal card already says
 * "template" — and the caller shows 2 rows instead of 3 (`fieldTemplateRows`).
 *
 * Sizes live here as constants rather than riding the pad's `type` ladder,
 * because this row also renders inside `LabListPicker` (Tailwind, no ladder).
 * They match the ladder's post-D22 values and clear its floors.
 *
 * D30 — the TWIN of the All-templates list row: title and sub-line move with
 * it (1rem / 0.875rem, card min-height 48px) so the same template cannot read
 * as two different things inside one visit. It stays LIGHTER than the list row
 * on purpose — no checkbox, no Edit/Delete, single-pick.
 */
export const TEMPLATE_ROW_TYPE = {
  title: '1rem',
  sub: '0.875rem',
  badge: '0.75rem',
  heading: '0.8125rem',
};

/** The twin's row box (52px in the list, 48px here — it carries less). */
export const TEMPLATE_ROW_MIN_HEIGHT = '3rem';

const TEAL = { light: '#0d9488', dark: '#2dd4bf' };

/** Teal-ink group heading — `TEMPLATE_ROW_LABELS`, never a free-handed string. */
export function TemplateGroupHeading({ label, isDark }) {
  return (
    <div
      data-testid="template-group-heading"
      style={{
        padding: '0.5rem 0.625rem 0.25rem',
        fontSize: TEMPLATE_ROW_TYPE.heading, fontWeight: 700,
        color: isDark ? TEAL.dark : TEAL.light,
        textTransform: 'uppercase', letterSpacing: '0.04em',
        fontFamily: 'system-ui, sans-serif',
      }}
    >
      {label}
    </div>
  );
}

/**
 * `See all n` — opens the All-templates list pre-filtered to this category.
 * D30: the SAME opener treatment the strip uses — an outlined button with a
 * list glyph and a chevron, never a `+ …` chip, because it opens a panel
 * instead of adding anything.
 */
export function TemplateSeeAllRow({
  count, category, isDark, onClick, open = false,
  // bk-989 — the opener is the LAST arrow-key entry of the group, so it takes
  // the same highlight treatment (a solid fill, never an opacity step), the
  // same scroll-into-view ref and the same real-movement hover as a row.
  highlighted = false, rowRef = null, onMouseMove = undefined,
}) {
  return (
    <div style={{ padding: '0.125rem 0.5rem 0.375rem' }}>
      <button
        type="button"
        data-testid="template-see-all"
        data-highlighted={highlighted ? 'true' : 'false'}
        ref={rowRef}
        onMouseMove={onMouseMove}
        // FE-27 — the menu pattern: a press inside a panel must NOT move focus
        // out of the field that owns it. Without this, a browser that does not
        // focus the pressed button (Safari, Firefox) fires `focusout` with a
        // null relatedTarget, the field closes its panel mid-press, and this
        // button is gone before the click lands — the owner's "See all does
        // nothing".
        onMouseDown={(e) => e.preventDefault()}
        onClick={onClick}
        aria-label={TEMPLATE_ROW_COPY.openerAriaLabel(count, category)}
        aria-expanded={open}
        title={TEMPLATE_ROW_COPY.openerAriaLabel(count, category)}
        style={{
          display: 'flex', alignItems: 'center', gap: '0.35rem',
          width: '100%', minHeight: '2rem', padding: '0.3rem 0.5rem', borderRadius: '0.375rem',
          border: `1px solid ${open || highlighted ? OPENER_TEAL : (isDark ? 'rgba(255,255,255,0.28)' : '#94a3b8')}`,
          background: open ? OPENER_TEAL : (highlighted
            ? (isDark ? 'rgba(13,148,136,0.34)' : '#ccfbf1')
            : (isDark ? '#1e293b' : '#ffffff')),
          color: open ? '#ffffff' : (isDark ? TEAL.dark : TEAL.light),
          fontSize: TEMPLATE_ROW_TYPE.sub, fontWeight: 700, cursor: 'pointer',
          textAlign: 'left', fontFamily: 'system-ui, sans-serif',
        }}
      >
        <ListGlyph />
        <span style={{ flex: 1 }}>{TEMPLATE_ROW_COPY.openerLabel(count)}</span>
        <Chevron open={open} />
      </button>
    </div>
  );
}

export default function TemplateSuggestionRow({
  template,
  category,
  narrow = false,
  highlighted = false,
  isDark = false,
  onPick,
  rowRef = null,
  onMouseMove = undefined,
}) {
  const { title, subline } = fieldTemplateRowText(template, category);
  return (
    <div style={{ padding: '0 0.5rem 0.25rem' }}>
      <button
        type="button"
        data-source="template_set"
        // The keyboard position, readable without parsing a colour.
        data-highlighted={highlighted ? 'true' : 'false'}
        ref={rowRef}
        // FE-27 — see the opener below: the press keeps focus in the field.
        onMouseDown={(e) => e.preventDefault()}
        onClick={onPick}
        onMouseMove={onMouseMove}
        title={TEMPLATE_ROW_COPY.chipAriaLabel(template)}
        style={{
          display: 'flex', alignItems: 'center', gap: '0.5rem', width: '100%',
          minHeight: TEMPLATE_ROW_MIN_HEIGHT,
          padding: '0.5rem 0.75rem', textAlign: 'left', cursor: 'pointer',
          border: `1.5px solid ${isDark ? 'rgba(45,212,191,0.5)' : 'rgba(13,148,136,0.45)'}`,
          borderRadius: '0.5rem',
          // The highlighted state is a different SOLID fill, never an opacity
          // change, so the keyboard position is readable in both modes.
          background: highlighted
            ? (isDark ? 'rgba(13,148,136,0.34)' : '#ccfbf1')
            : (isDark ? 'rgba(13,148,136,0.22)' : '#f0fdfa'),
          fontFamily: 'system-ui, sans-serif',
        }}
      >
        <span style={{
          width: '1.75rem', height: '1.75rem', borderRadius: '0.375rem',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          background: TEAL.light, color: '#fff', fontSize: TEMPLATE_ROW_TYPE.title, flexShrink: 0,
        }}>📋</span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{
            display: 'block', fontWeight: 700, fontSize: TEMPLATE_ROW_TYPE.title,
            color: isDark ? '#f1f5f9' : '#0f172a',
          }}>
            {title}
          </span>
          {!narrow && (
            <span style={{
              display: 'block', fontSize: TEMPLATE_ROW_TYPE.sub,
              color: isDark ? '#cbd5e1' : '#475569',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {subline}
            </span>
          )}
        </span>
        {!narrow && (
          <span style={{
            fontSize: TEMPLATE_ROW_TYPE.badge, fontWeight: 700, color: '#fff',
            background: TEAL.light, borderRadius: '2rem', padding: '0.1rem 0.5rem', flexShrink: 0,
          }}>Saved</span>
        )}
      </button>
    </div>
  );
}
